import { useEffect, useState } from 'react'
import { fetchFreeTrucks, type FleetLoad } from '../../api/assignDriverApi'
import { candidatesOf, type AssignCandidate } from './assignModel'

/** The pause after the last keystroke before the search is sent. */
const SEARCH_DEBOUNCE_MS = 250

interface PickerAnswer {
    /** The server's trucks for the search, in its order; undefined until the first answer. */
    candidates: AssignCandidate[] | undefined
    /** The load the picker is for: its pickup place, time and whether it has passed. */
    load?: FleetLoad
    error: boolean
}

interface AssignCandidatesState extends PickerAnswer {
    /** The search box's text. */
    query: string
    setQuery: (query: string) => void
}

const EMPTY: PickerAnswer = { candidates: undefined, error: false }

/**
 * The trucks that can take a load, read from the server while the picker is open. The search box's text goes to the server as
 * `params.search` after a short pause; the list is the server's, never filtered here.
 */
export function useAssignCandidates(loadId: string): AssignCandidatesState {
    const [query, setQuery] = useState('')
    const [search, setSearch] = useState('')
    const [state, setState] = useState<PickerAnswer>(EMPTY)

    useEffect(() => {
        const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(timer)
    }, [query])

    useEffect(() => {
        let live = true
        fetchFreeTrucks(loadId, search || undefined)
            .then((answer) => {
                if (live) setState({ candidates: candidatesOf(answer.candidates), load: answer.load, error: false })
            })
            .catch(() => {
                if (live) setState({ candidates: undefined, error: true })
            })
        return () => {
            live = false
        }
    }, [loadId, search])

    return { ...state, query, setQuery }
}
